import css from "./styles/studio.css?inline";
import { DEVICES, DEVICE_COUNT, DEFAULT_DEVICE_IDS } from "./data/devices";
import { SAMPLE_HTML } from "./data/sample";
import { copyText, decodeShare, downloadHtml, encodeShare, readShareHash } from "./core/share";
import type { MountOptions, StudioHandle } from "./types";
import { createStudio } from "./ui/App";

export { DEVICES, DEVICE_COUNT, SAMPLE_HTML };
export { SAMPLE_TEMPLATES } from "./data/templates";
export type { SampleTemplate } from "./data/templates";
export { DESKTOP_VIEWPORTS } from "./data/viewports";
export { copyText, decodeShare, downloadHtml, encodeShare, readShareHash };
export type { Device, DeviceCategory, MountOptions, StudioHandle, RuntimeOptions, CustomViewport, BrowserViewport, PreviewViewport, ViewportCategory } from "./types";

export const VERSION = "1.1.0";

const ELEMENT_NAME = "html-device-preview";

function resolveTarget(target: string | HTMLElement): HTMLElement {
  const el = typeof target === "string" ? document.querySelector(target) : target;
  if (!(el instanceof HTMLElement)) {
    throw new Error("HtmlDevicePreview: mount target not found");
  }
  return el;
}

const mounted = new WeakMap<HTMLElement, StudioHandle>();

function mountInto(host: HTMLElement, options: MountOptions = {}): StudioHandle {
  mounted.get(host)?.destroy();
  const shadow = host.shadowRoot ?? host.attachShadow({ mode: "open" });
  const style = document.createElement("style");
  style.textContent = css;
  const wrap = document.createElement("div");
  wrap.style.display = "flex";
  wrap.style.flexDirection = "column";
  wrap.style.minHeight = "100%";
  wrap.style.height = "100%";
  shadow.replaceChildren(style, wrap);
  if (options.height) host.style.height = options.height;
  if (!host.style.minHeight) host.style.minHeight = "640px";
  const handle = createStudio(wrap, options);
  mounted.set(host, handle);
  return handle;
}

export function mount(
  target: string | HTMLElement,
  options: MountOptions = {},
): StudioHandle {
  return mountInto(resolveTarget(target), options);
}

function readElementOptions(el: HTMLElement): MountOptions {
  const devices = el.getAttribute("devices");
  return {
    html: el.getAttribute("html") ?? undefined,
    theme: el.getAttribute("theme") === "dark" ? "dark" : "light",
    editorMode: el.getAttribute("editor-mode") === "visual" ? "visual" : "source",
    devices: devices ? devices.split(",").map((s) => s.trim()).filter(Boolean) : undefined,
    mode: (el.getAttribute("mode") as MountOptions["mode"]) ?? undefined,
    orientation:
      (el.getAttribute("orientation") as MountOptions["orientation"]) ?? undefined,
    zoom: (el.getAttribute("zoom") as MountOptions["zoom"]) ?? undefined,
    showEditor: el.getAttribute("hide-editor") == null,
    showChrome: el.getAttribute("hide-chrome") == null,
    height: el.getAttribute("height") ?? undefined,
    filename: el.getAttribute("filename") ?? undefined,
  };
}

class HtmlDevicePreviewElement extends HTMLElement {
  private handle: StudioHandle | undefined;

  connectedCallback(): void {
    this.handle = mountInto(this, readElementOptions(this));
  }

  disconnectedCallback(): void {
    this.handle?.destroy();
    this.handle = undefined;
  }

  static get observedAttributes(): string[] {
    return ["html", "theme", "devices", "mode", "orientation", "zoom", "hide-editor"];
  }

  attributeChangedCallback(name: string, oldValue: string | null, next: string | null): void {
    if (!this.handle || oldValue === next) return;
    if (name === "html") this.handle.setHtml(next ?? "");
    else {
      const options = readElementOptions(this);
      if (name === "theme") this.handle.setOptions({ theme: options.theme });
      if (name === "devices") this.handle.setOptions({ devices: options.devices ?? [...DEFAULT_DEVICE_IDS] });
      if (name === "mode") this.handle.setOptions({ mode: options.mode ?? "grid" });
      if (name === "orientation") this.handle.setOptions({ orientation: options.orientation ?? "portrait" });
      if (name === "zoom") this.handle.setOptions({ zoom: options.zoom ?? "fit" });
      if (name === "hide-editor") this.handle.setOptions({ showEditor: options.showEditor });
    }
  }
}

export function defineElement(name = ELEMENT_NAME): void {
  if (customElements.get(name)) return;
  customElements.define(name, class extends HtmlDevicePreviewElement {});
}

defineElement();
